import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ClearAll,
  Inventory2,
  ReceiptLong,
  Refresh,
  Search,
  Storefront,
  WarningAmber,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import API from '../services/api';
import { addTab } from '../tabs/tabsSlice';
import MainText from '../components/MainText';
import DatePick from '../components/DatePick';
import CustomSnackBar from '../components/CustomSnackBar';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(value || 0));

const formatNumber = (value) => Number(value || 0).toLocaleString('es-GT');

const todayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toLocalDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return todayLocal();
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatShortDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('es-GT');
};

const formatDateTime = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '—';
  return value.toLocaleString('es-GT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getSupplierLabel = (value) =>
  value?.supplier?.companyName || value?.supplier?.legalName || value?.supplier_name || 'Sin proveedor';

const getFormatLabel = (value) =>
  value?.prformat?.name || value?.presentation || value?.unit || 'Sin formato';

function MetricCard({ label, value, helper, accent = '#10173c' }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        borderColor: (theme) => alpha(theme.palette.divider, 0.8),
        boxShadow: '0 8px 24px rgba(16,23,60,0.04)',
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: 24, fontWeight: 700, color: accent }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
        {helper}
      </Typography>
    </Paper>
  );
}

function EmptyState({ icon, title, description }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        p: { xs: 3, md: 5 },
        borderRadius: 3,
        borderStyle: 'dashed',
        textAlign: 'center',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(16,23,60,0.08)',
          color: '#10173c',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#10173c' }}>
        {title}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: 14, color: 'text.secondary', maxWidth: 620, mx: 'auto' }}>
        {description}
      </Typography>
    </Paper>
  );
}

function SelectedProductCard({ product, lastFetchedAt }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 3,
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: 'text.secondary' }}>
        PRODUCTO SELECCIONADO
      </Typography>

      <Box
        sx={{
          mt: 1.25,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#10173c' }}>
            {product?.description || 'Producto'}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary', fontFamily: 'monospace' }}>
            {product?.code || 'Sin código'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip size="small" label={getFormatLabel(product)} />
          <Chip
            size="small"
            label={product?.is_active === false ? 'Inactivo' : 'Activo'}
            color={product?.is_active === false ? 'default' : 'success'}
            variant={product?.is_active === false ? 'outlined' : 'filled'}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          <strong>Proveedor:</strong> {getSupplierLabel(product)}
        </Typography>
        {lastFetchedAt ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            <strong>Última consulta:</strong> {formatDateTime(lastFetchedAt)}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

export default function KardexRecepciones() {
  const theme = useTheme();
  const initialFilters = useMemo(() => ({
    dateFrom: todayLocal(),
    dateTo: todayLocal(),
  }), []);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productInput, setProductInput] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productLoading, setProductLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [hasConsulted, setHasConsulted] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('info');

  const dispatch = useDispatch();

  const showSnack = useCallback((message, severity = 'info') => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  }, []);

  useEffect(() => {
    dispatch(addTab({ label: 'Kardex recepciones', path: '/orders/kardex/recepciones' }));
  }, [dispatch]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setProductSearch(productInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [productInput]);

  useEffect(() => {
    const query = String(productSearch || '').trim();
    if (query.length < 2) {
      setProductLoading(false);
      setProductOptions(selectedProduct ? [selectedProduct] : []);
      return undefined;
    }

    const controller = new AbortController();
    let mounted = true;

    setProductLoading(true);

    API.get('product/', {
      params: {
        search: query,
        limit: 8,
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        if (!mounted) return;
        const fetched = Array.isArray(data) ? data : data?.results || [];
        const merged = selectedProduct
          ? [selectedProduct, ...fetched.filter((item) => item?.id !== selectedProduct?.id)]
          : fetched;
        setProductOptions(merged);
      })
      .catch((error) => {
        if (!mounted) return;
        if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return;
        }
        setProductOptions(selectedProduct ? [selectedProduct] : []);
        showSnack('No se pudo buscar productos', 'error');
      })
      .finally(() => {
        if (mounted) setProductLoading(false);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [productSearch, selectedProduct, showSnack]);

  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    return list.sort((a, b) => {
      const left = new Date(`${a?.date || ''}T00:00:00`).getTime();
      const right = new Date(`${b?.date || ''}T00:00:00`).getTime();
      if (right !== left) return right - left;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  }, [rows]);

  const summary = useMemo(() => {
    const totalInvoices = new Set(sortedRows.map((row) => row?.invoice_id || `${row?.invoice_series}-${row?.invoice_number}`)).size;
    const totalUnits = sortedRows.reduce((acc, row) => acc + Number(row?.effective_quantity || row?.quantity_re || row?.quantity || 0), 0);
    const totalValor = sortedRows.reduce((acc, row) => {
      const rowTotal = Number(row?.total);
      if (Number.isFinite(rowTotal)) return acc + rowTotal;
      return acc + (Number(row?.effective_quantity || row?.quantity_re || row?.quantity || 0) * Number(row?.price || 0));
    }, 0);
    const latestCost = sortedRows.length ? Number(sortedRows[0]?.price || 0) : 0;
    const supplierCount = new Set(sortedRows.map((row) => row?.supplier_id || row?.supplier_name).filter(Boolean)).size;
    const averageCost = totalUnits > 0 ? totalValor / totalUnits : 0;

    return {
      totalInvoices,
      totalUnits,
      totalValor,
      latestCost,
      averageCost,
      supplierCount,
    };
  }, [sortedRows]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setRows([]);
    setHasConsulted(false);
    setPage(0);
  };

  const handleProductSelect = (_, value) => {
    setSelectedProduct(value || null);
    setRows([]);
    setHasConsulted(false);
    setPage(0);
  };

  const handleClear = () => {
    setSelectedProduct(null);
    setProductInput('');
    setProductSearch('');
    setProductOptions([]);
    setFilters(initialFilters);
    setRows([]);
    setHasConsulted(false);
    setLastFetchedAt(null);
    setPage(0);
  };

  const fetchData = useCallback(async () => {
    if (!selectedProduct?.id) {
      showSnack('Selecciona un producto para consultar su trazabilidad', 'warning');
      return;
    }

    setLoading(true);
    try {
      const params = {
        product_id: selectedProduct.id,
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
      };

      const { data } = await API.get('kardex/recepciones/', { params });
      const list = Array.isArray(data) ? data : data?.results || [];
      setRows(list);
      setHasConsulted(true);
      setLastFetchedAt(new Date());
      setPage(0);
    } catch (error) {
      console.error('Error cargando kardex por producto', error);
      setRows([]);
      showSnack('No se pudo cargar la trazabilidad del producto', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters.dateFrom, filters.dateTo, selectedProduct, showSnack]);

  const headerBorder = alpha(theme.palette.divider, 0.8);
  const tableHover = alpha(theme.palette.primary.main, 0.035);
  const subtleBg = theme.palette.background?.secondary || alpha(theme.palette.primary.main, 0.035);

  return (
    <>
      <MainText
        title="Kardex por producto"
        subtitle="Consulta la trazabilidad de recepciones, facturas, costos y detalle operativo de un producto dentro de un período."
        icon={Inventory2}
        width="auto"
      />

      <Paper
        variant="outlined"
        sx={{
          mt: 1.5,
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          borderColor: headerBorder,
          background: `linear-gradient(180deg, ${alpha('#10173c', 0.02)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
          boxShadow: '0 16px 40px rgba(16,23,60,0.05)',
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.9, color: 'text.secondary' }}>
          CONSULTA DE TRAZABILIDAD
        </Typography>

        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(320px, 2.3fr) repeat(2, minmax(170px, 1fr)) auto auto',
            },
            alignItems: 'center',
          }}
        >
          <Autocomplete
            options={productOptions}
            filterOptions={(options) => options}
            loading={productLoading}
            value={selectedProduct}
            inputValue={productInput}
            onInputChange={(_, value, reason) => {
              setProductInput(value);
              if (reason === 'clear') {
                setSelectedProduct(null);
                setRows([]);
                setHasConsulted(false);
              } else if (reason === 'input' && selectedProduct && value !== (selectedProduct.description || '')) {
                setSelectedProduct(null);
                setRows([]);
                setHasConsulted(false);
              }
            }}
            onChange={handleProductSelect}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => option?.description || ''}
            noOptionsText={productInput.trim().length < 2 ? 'Escribe al menos 2 caracteres' : 'Sin coincidencias'}
            loadingText="Buscando productos..."
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} sx={{ py: 1.2 }}>
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#10173c' }}>
                    {option.description}
                  </Typography>
                  <Box sx={{ mt: 0.4, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
                      {option.code}
                    </Typography>
                    <Chip size="small" label={getFormatLabel(option)} />
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {getSupplierLabel(option)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar producto"
                placeholder="Nombre, código o presentación..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {productLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <DatePick
            label="Fecha desde"
            size="small"
            value={filters.dateFrom}
            onChange={(value) => handleFilterChange('dateFrom', value ? toLocalDate(value) : todayLocal())}
            allowHistoricalDates
            allowFutureDates={false}
            maxWidth={210}
            minWidth={170}
          />

          <DatePick
            label="Fecha hasta"
            size="small"
            value={filters.dateTo}
            onChange={(value) => handleFilterChange('dateTo', value ? toLocalDate(value) : todayLocal())}
            allowHistoricalDates
            allowFutureDates={false}
            maxWidth={210}
            minWidth={170}
          />

          <Button
            variant="contained"
            onClick={fetchData}
            disabled={loading || !selectedProduct?.id}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            sx={{
              minHeight: 40,
              px: 2,
              backgroundColor: '#10173c',
              '&:hover': { backgroundColor: '#0b112c' },
            }}
          >
            Consultar
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<ClearAll />}
            sx={{ minHeight: 40, px: 2 }}
          >
            Limpiar
          </Button>
        </Box>

        {selectedProduct ? <SelectedProductCard product={selectedProduct} lastFetchedAt={lastFetchedAt} /> : null}
      </Paper>

      {!selectedProduct ? (
        <EmptyState
          icon={<Storefront />}
          title="Busca un producto para iniciar la consulta"
          description="Selecciona un producto y define un rango de fechas para consultar su trazabilidad de recepciones, facturas, costos y detalle operativo."
        />
      ) : loading ? (
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            borderColor: headerBorder,
          }}
        >
          <CircularProgress size={26} />
          <Typography sx={{ mt: 1.5, fontSize: 14, color: 'text.secondary' }}>
            Consultando trazabilidad del producto seleccionado...
          </Typography>
        </Paper>
      ) : !hasConsulted ? (
        <EmptyState
          icon={<ReceiptLong />}
          title="Producto listo para consultar"
          description="Ajusta el rango de fechas y presiona Consultar para ver entradas, facturas, costos, responsables y destino de recepción."
        />
      ) : (
        <>
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(6, minmax(0, 1fr))',
              },
            }}
          >
            <MetricCard
              label="FACTURAS"
              value={formatNumber(summary.totalInvoices)}
              helper="Documentos donde aparece el producto"
            />
            <MetricCard
              label="UNIDADES RECIBIDAS"
              value={formatNumber(summary.totalUnits)}
              helper="Cantidad recibida en el período"
            />
            <MetricCard
              label="COSTO PROMEDIO"
              value={formatMoney(summary.averageCost)}
              helper="Promedio ponderado por unidades"
              accent={theme.palette.primary.main}
            />
            <MetricCard
              label="ÚLTIMO COSTO"
              value={formatMoney(summary.latestCost)}
              helper="Costo unitario más reciente"
              accent={theme.palette.primary.main}
            />
            <MetricCard
              label="TOTAL VALORIZADO"
              value={formatMoney(summary.totalValor)}
              helper="Valor total del producto recibido"
              accent={theme.palette.success.main}
            />
            <MetricCard
              label="PROVEEDORES"
              value={formatNumber(summary.supplierCount)}
              helper="Proveedores involucrados"
            />
          </Box>

          {!sortedRows.length ? (
            <EmptyState
              icon={<WarningAmber />}
              title="Sin recepciones en este período"
              description="No se encontraron entradas del producto seleccionado dentro del rango consultado. Ajusta fechas o verifica que el producto tenga movimientos."
            />
          ) : (
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 3,
                overflow: 'hidden',
                borderColor: headerBorder,
                boxShadow: '0 18px 42px rgba(16,23,60,0.05)',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: 1,
                  flexDirection: { xs: 'column', md: 'row' },
                  borderBottom: '1px solid',
                  borderColor: headerBorder,
                  bgcolor: subtleBg,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#10173c' }}>
                    Trazabilidad de recepciones
                  </Typography>
                  <Typography sx={{ mt: 0.3, fontSize: 12, color: 'text.secondary' }}>
                    {sortedRows.length} movimientos encontrados para {selectedProduct?.description}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Rango consultado: {formatShortDate(filters.dateFrom)} a {formatShortDate(filters.dateTo)}
                </Typography>
              </Box>

              <Box sx={{ overflowX: 'auto', bgcolor: 'background.paper' }}>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    minWidth: 1480,
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    '& thead th': {
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: '#10173c',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.7,
                      textAlign: 'left',
                      px: 1.75,
                      py: 1.4,
                      borderBottom: `1px solid ${alpha('#10173c', 0.9)}`,
                      whiteSpace: 'nowrap',
                    },
                    '& tbody td': {
                      px: 1.75,
                      py: 1.6,
                      fontSize: 13,
                      verticalAlign: 'top',
                      borderBottom: `1px solid ${headerBorder}`,
                    },
                    '& tbody tr:hover': {
                      backgroundColor: tableHover,
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Factura</th>
                      <th>Serie</th>
                      <th>Proveedor</th>
                      <th>Producto</th>
                      <th>Formato</th>
                      <th style={{ textAlign: 'right' }}>Cantidad recibida</th>
                      <th>Unidad</th>
                      <th style={{ textAlign: 'right' }}>Costo unitario</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th>Firmó / recibió</th>
                      <th>Bodega o destino</th>
                      <th>Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row) => {
                      const qty = Number(row?.effective_quantity || row?.quantity_re || row?.quantity || 0);
                      const invoiceQty = Number(row?.quantity || 0);
                      const total = Number(row?.total || (qty * Number(row?.price || 0)));

                      return (
                        <tr key={row.id}>
                          <td>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                              {formatShortDate(row.date)}
                            </Typography>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color: '#10173c' }}>
                              {row.invoice_number || '—'}
                            </Typography>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {row.invoice_series || '—'}
                            </Typography>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                              {row.supplier_name || '—'}
                            </Typography>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                              {row.product_description || '—'}
                            </Typography>
                            <Typography sx={{ mt: 0.4, fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
                              {row.product_code || '—'}
                            </Typography>
                          </td>
                          <td>
                            <Chip size="small" label={row.presentation || 'Sin formato'} />
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#10173c' }}>
                              {formatNumber(qty)}
                            </Typography>
                            {invoiceQty !== qty ? (
                              <Typography sx={{ mt: 0.4, fontSize: 11, color: 'text.secondary' }}>
                                Facturada: {formatNumber(invoiceQty)}
                              </Typography>
                            ) : null}
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {row.unit || '—'}
                            </Typography>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.palette.primary.main }}>
                              {formatMoney(row.price)}
                            </Typography>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.palette.success.main }}>
                              {formatMoney(total)}
                            </Typography>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 13 }}>
                              {row.signed_by_name || '—'}
                            </Typography>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 13 }}>
                              {row.warehouse_name || 'No aplica'}
                            </Typography>
                            {Array.isArray(row.receiving_note_numbers) && row.receiving_note_numbers.length ? (
                              <Typography sx={{ mt: 0.4, fontSize: 11, color: 'text.secondary' }}>
                                {row.receiving_note_numbers.join(', ')}
                              </Typography>
                            ) : null}
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 12, color: row.observations ? 'text.primary' : 'text.secondary' }}>
                              {row.observations || 'Sin observaciones'}
                            </Typography>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Box>
              </Box>

              <TablePagination
                component="div"
                count={sortedRows.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Filas"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  borderTop: '1px solid',
                  borderColor: headerBorder,
                  bgcolor: subtleBg,
                }}
              />
            </Paper>
          )}
        </>
      )}

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMessage} severity={snackSeverity} />
    </>
  );
}
